/*****************************************************
 * CustomerAnalytics.jsx — v3.0
 * Volledig analytics dashboard met taart/donut/balk
 * keuze per grafiek, routes, gewicht, doorlooptijd
 *****************************************************/
import { useLanguage } from '../LanguageContext';
import React, { useState, useMemo } from 'react';

const G = {
  navy:'#1a364c', navyMid:'#24496a', navyLight:'#2d5a7a',
  gold:'#d4a848', goldLight:'#f0c96a',
  white:'#ffffff', cream:'#f7f6f2', borderL:'#e8edf3',
  green:'#10b981', amber:'#f59e0b', red:'#ef4444', blue:'#3b82f6',
  txt:'#1e293b', txt2:'#64748b', txt3:'#94a3b8',
};

const PALETTE_COLORS = {
  sendwell: ['#1a364c','#d4a848','#10b981','#c2e6ef','#f59e0b','#8b5cf6','#24496a','#f0c96a','#3b82f6','#2d5a7a','#34d399','#a78bfa'],
  ocean:    ['#0ea5e9','#0284c7','#0369a1','#38bdf8','#7dd3fc','#bae6fd','#06b6d4','#22d3ee','#67e8f9','#a5f3fc','#0891b2','#164e63'],
  sunset:   ['#ef4444','#f97316','#f59e0b','#eab308','#84cc16','#22c55e','#06b6d4','#3b82f6','#8b5cf6','#ec4899','#f43f5e','#fb923c'],
  forest:   ['#166534','#15803d','#16a34a','#22c55e','#4ade80','#86efac','#365314','#4d7c0f','#65a30d','#a3e635','#84cc16','#bef264'],
  purple:   ['#581c87','#7e22ce','#9333ea','#a855f7','#c084fc','#e9d5ff','#4c1d95','#6d28d9','#7c3aed','#8b5cf6','#ddd6fe','#ede9fe'],
  contrast: ['#1a364c','#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#06b6d4','#84cc16','#f97316','#6366f1','#14b8a6'],
};
// Labels worden via t() vertaald binnen het component (keys: pal_oceaan, pal_zonsondergang, etc.)

const MAANDEN_NL = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec']; // fallback

const fmt  = (n) => `€ ${Math.round(n).toLocaleString('nl-NL')}`;
const fmtK = (n) => { if(n===0)return '€ 0'; return n>=1000?`€ ${(n/1000).toFixed(1)}k`:`€ ${Math.round(n)}`; };
const fmtN  = (n) => `${Math.round(n)}`;
const fmtKg = (n) => n>=1000?`${(n/1000).toFixed(1)}t`:`${Math.round(n)}kg`;

function isOnderweg(a) {
  const sl=(a.Status||'').toLowerCase();
  if(sl.includes('onderweg'))return true;
  if(sl.includes('opdracht verzonden')||sl.includes('opdracht bevestigd')){
    if(a.LaadDatum){
      const l=new Date(a.LaadDatum);l.setHours(0,0,0,0);
      const v=new Date();v.setHours(0,0,0,0);
      return l<=v;
    }
  }
  return false;
}

// ─── Chart type toggle ───────────────────────────────────────────────────────
const ChartToggle = ({ value, onChange }) => {
  const { t } = useLanguage();
  return (
  <div style={{ display:'flex', background:'#f1f5f9', borderRadius:6, padding:2, gap:1 }}>
    {[['taart','🥧', t('cht_taart')||'Pie'],['donut','⭕', t('cht_donut')||'Donut'],['balk','📊', t('cht_balk')||'Bar']].map(([type, icon, label]) => (
      <button key={type} onClick={() => onChange(type)} style={{
        padding:'3px 10px', border:'none', borderRadius:5,
        background: value===type ? G.white : 'transparent',
        boxShadow: value===type ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
        fontSize:11, cursor:'pointer', color: value===type ? G.navy : G.txt3,
        fontWeight: value===type ? 600 : 400, fontFamily:'inherit', transition:'.12s',
      }} title={label}>
        {icon} {label}
      </button>
    ))}
  </div>
);
}
// ─── Taart / Donut ───────────────────────────────────────────────────────────
const PieChart = ({ slices, donut=false, formatVal }) => {
  const { t } = useLanguage();
  const [hov, setHov] = useState(null);
  const total = slices.reduce((s,v)=>s+v.value,0)||1;
  const r = donut ? 58 : 68;
  const cx=90, cy=90;
  let angle=-Math.PI/2;

  const arcs = slices.map((sl,i) => {
    const pct=sl.value/total;
    const a1=angle, a2=angle+pct*2*Math.PI;
    angle=a2;
    const mid=(a1+a2)/2;
    const x1=cx+r*Math.cos(a1),y1=cy+r*Math.sin(a1);
    const x2=cx+r*Math.cos(a2),y2=cy+r*Math.sin(a2);
    const large=pct>.5?1:0;
    const lr=donut?r-12:r*0.65;
    return {...sl,i,d:`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      arc:`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
      pct, mid, lx:cx+lr*Math.cos(mid), ly:cy+lr*Math.sin(mid)};
  });

  return (
    <div style={{ display:'flex', alignItems:'center', gap:16 }}>
      <svg width={180} height={180} style={{ flexShrink:0 }}>
        {arcs.map((arc,i) => (
          <g key={i} onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)} style={{cursor:'default'}}>
            {donut ? (
              <path d={arc.arc} fill="none" stroke={arc.color}
                strokeWidth={hov===i?22:16} strokeLinecap="butt" style={{transition:'stroke-width .15s'}}/>
            ) : (
              <path d={arc.d} fill={arc.color}
                transform={hov===i?`translate(${Math.cos(arc.mid)*5},${Math.sin(arc.mid)*5})`:''}
                style={{transition:'transform .15s'}} stroke={G.white} strokeWidth="1.5"/>
            )}
            {arc.pct>0.08&&!donut&&(
              <text x={arc.lx} y={arc.ly} fontSize="10" fill="white" textAnchor="middle"
                dominantBaseline="middle" fontWeight="600" pointerEvents="none">
                {Math.round(arc.pct*100)}%
              </text>
            )}
          </g>
        ))}
        {donut&&(
          <>
            <text x={cx} y={cy-7} fontSize="20" fontWeight="700" fill={G.txt} textAnchor="middle">{slices.length}</text>
            <text x={cx} y={cy+12} fontSize="10" fill={G.txt3} textAnchor="middle">{t('analytisch_categorieren')}</text>
          </>
        )}
        {hov!==null&&(
          <g>
            <rect x={cx-46} y={-28} width={92} height={22} rx="4" fill={G.navy} opacity=".92"/>
            <text x={cx} y={-13} fontSize="11" fill="white" textAnchor="middle" fontWeight="600">
              {arcs[hov]?.label}: {formatVal?formatVal(arcs[hov]?.value):arcs[hov]?.value}
            </text>
          </g>
        )}
      </svg>
      <div style={{flex:1,minWidth:0}}>
        {slices.map((sl,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:7,marginBottom:7,
            opacity:hov!==null&&hov!==i?.5:1,transition:'opacity .15s'}}>
            <div style={{width:10,height:10,borderRadius:donut?'50%':2,background:sl.color,flexShrink:0}}/>
            <span style={{flex:1,fontSize:12.5,color:G.txt,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{sl.label}</span>
            <span style={{fontSize:12,fontWeight:600,color:G.txt,flexShrink:0}}>{formatVal?formatVal(sl.value):sl.value}</span>
            <span style={{fontSize:11,color:G.txt3,width:34,textAlign:'right',flexShrink:0}}>{Math.round((sl.value/total)*100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Staafgrafiek ─────────────────────────────────────────────────────────────
const BarChart = ({ data, labels, color=G.navy, height=150, formatY=fmtK, highlightIndex }) => {
  const [hov,setHov]=useState(null);
  const max=Math.max(...data.filter(Boolean),1);
  const n=data.length, W=520, PAD=46;
  const barW=Math.min(Math.floor((W/n)*0.6),52);

  return (
    <svg width="100%" viewBox={`0 0 ${W+PAD+16} ${height+52}`} style={{overflow:'visible',display:'block'}}>
      {[0,.25,.5,.75,1].map(pct=>(
        <g key={pct}>
          <line x1={PAD} y1={height-pct*height+4} x2={W+PAD} y2={height-pct*height+4} stroke="#f1f5f9" strokeWidth="1"/>
          <text x={PAD-6} y={height-pct*height+8} fontSize="10" fill={G.txt3} textAnchor="end">{formatY(max*pct)}</text>
        </g>
      ))}
      {data.map((val,i)=>{
        const barH=val?Math.max((val/max)*height,3):0;
        const slotW=W/n, x=PAD+i*slotW+(slotW-barW)/2, y=height-barH+4;
        const isHov=hov===i, isHL=highlightIndex===i;
        const fill=isHov||isHL?G.gold:(val?color:'#f1f5f9');
        return (
          <g key={i} style={{cursor:'default'}} onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}>
            <rect x={x} y={y} width={barW} height={barH||3} rx="3" fill={fill} opacity={val?1:0.3} style={{transition:'fill .15s'}}/>
            {isHov&&val>0&&(
              <g>
                <rect x={x+barW/2-38} y={y-26} width={76} height={20} rx="4" fill={G.navy}/>
                <text x={x+barW/2} y={y-12} fontSize="11" fill="white" textAnchor="middle" fontWeight="600">{formatY(val)}</text>
              </g>
            )}
            <text x={x+barW/2} y={height+20} fontSize="10" fill={isHL?G.navy:G.txt3}
              textAnchor="middle" fontWeight={isHL?'700':'400'}>{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── Horizontale balken ───────────────────────────────────────────────────────
const HBars = ({ items, formatVal, palette }) => {
  const max=Math.max(...items.map(i=>i.value),1);
  const total=items.reduce((s,i)=>s+i.value,0)||1;
  return (
    <div>
      {items.map((item,i)=>(
        <div key={i} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
          <div style={{width:10,height:10,borderRadius:2,background:item.color||(palette||[])[i]||'#1a364c',flexShrink:0}}/>
          <span style={{width:110,fontSize:12.5,color:G.txt,flexShrink:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.label}</span>
          <div style={{flex:1,height:8,background:'#f1f5f9',borderRadius:4,overflow:'hidden'}}>
            <div style={{width:`${(item.value/max)*100}%`,height:'100%',background:item.color||(palette||[])[i]||'#1a364c',borderRadius:4,transition:'.4s'}}/>
          </div>
          <span style={{fontSize:12,fontWeight:600,color:G.txt,width:60,textAlign:'right',flexShrink:0}}>{formatVal?formatVal(item.value):item.value}</span>
          <span style={{fontSize:11,color:G.txt3,width:32,textAlign:'right',flexShrink:0}}>{Math.round((item.value/total)*100)}%</span>
        </div>
      ))}
    </div>
  );
};

// ─── Universele grafiek-wrapper ───────────────────────────────────────────────
const FlexChart = ({ title, slices, formatVal, defaultType='donut', sub, palette }) => {
  const { t } = useLanguage();
  const [type,setType]=useState(defaultType);
  const slicesC=slices.map((s,i)=>({...s,color:s.color||(palette||[])[i]||'#1a364c'}));
  return (
    <div style={{background:G.white,border:`1px solid ${G.borderL}`,borderRadius:10,padding:'18px 20px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:G.txt2,letterSpacing:'.07em',textTransform:'uppercase'}}>{title}</div>
          {sub&&<div style={{fontSize:11,color:G.txt3,marginTop:2}}>{sub}</div>}
        </div>
        <ChartToggle value={type} onChange={setType}/>
      </div>
      {slices.length===0
        ? <div style={{textAlign:'center',padding:'28px',color:G.txt3,fontSize:13}}>{t('nog_geen_data')}</div>
        : type==='balk'
          ? <HBars items={slicesC} formatVal={formatVal}/>
          : <PieChart slices={slicesC} donut={type==='donut'} formatVal={formatVal}/>
      }
    </div>
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, accent, icon }) => (
  <div style={{background:G.white,borderRadius:10,padding:'16px 18px',border:`1px solid ${G.borderL}`,position:'relative',overflow:'hidden'}}>
    <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:accent,borderRadius:'10px 10px 0 0'}}/>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
      <div style={{fontSize:11,color:G.txt3,fontWeight:600,letterSpacing:'.06em',textTransform:'uppercase',marginTop:2}}>{label}</div>
      {icon&&<div style={{fontSize:18}}>{icon}</div>}
    </div>
    <div style={{fontSize:26,fontWeight:700,color:G.txt,letterSpacing:'-.5px',lineHeight:1,marginTop:8}}>{value}</div>
    {sub&&<div style={{fontSize:12,marginTop:6,color:G.txt3}}>{sub}</div>}
  </div>
);

// ─── Sectielabel ──────────────────────────────────────────────────────────────
const SectionLabel = ({children}) => (
  <div style={{fontSize:10,fontWeight:700,color:G.txt3,letterSpacing:'.1em',textTransform:'uppercase',
    marginBottom:10,marginTop:22,paddingBottom:6,borderBottom:`1px solid ${G.borderL}`}}>
    {children}
  </div>
);

// ─── Hoofdcomponent ────────────────────────────────────────────────────────────
export default function CustomerAnalytics({
  aanvragen = [] }) {
  const { t } = useLanguage();
  const [periode,setPeriode]=useState('maand');
  const [paletteKey,setPaletteKey]=useState('sendwell');
  const PALETTE = PALETTE_COLORS[paletteKey];
  const PALETTE_LABELS = useMemo(() => ({
    sendwell: 'SendWell',
    ocean: t('pal_oceaan'),
    sunset: t('pal_zonsondergang'),
    forest: t('pal_bos'),
    purple: t('pal_paars'),
    contrast: t('pal_contrast'),
  }), [t]);
  const nu=new Date();

  const data=useMemo(()=>{
    const ditJaar=nu.getFullYear(), huidigeMaand=nu.getMonth();
    const jaarRange=[ditJaar-2,ditJaar-1,ditJaar];
    const kostenMaand=Array(12).fill(0), volMaand=Array(12).fill(0), gewMaand=Array(12).fill(0);
    const kostenKwart=Array(4).fill(0),  volKwart=Array(4).fill(0);
    const kostenJaar=Array(3).fill(0),   volJaar=Array(3).fill(0);
    const landKosten={},landVol={},routeKosten={},routeVol={},goedMap={},statusMap={};
    let totGewicht=0,totColli=0,totDoorlooptijd=0,aantalDoorlooptijd=0;

    aanvragen.forEach(a=>{
      const datum=new Date(a.Datum||a.LaadDatum||0);
      const mnd=datum.getMonth(),jaar=datum.getFullYear(),kwart=Math.floor(mnd/3);
      const bedrag=parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0;
      if(jaar===ditJaar){
        kostenMaand[mnd]+=bedrag; volMaand[mnd]++;
        kostenKwart[kwart]+=bedrag; volKwart[kwart]++;
      }
      const ji=jaarRange.indexOf(jaar);
      if(ji>=0){kostenJaar[ji]+=bedrag;volJaar[ji]++;}
      [1,2,3,4,5].forEach(n=>{
        const g=parseFloat(a[`Goed${n}_Gewicht`])||0;
        const c=parseInt(a[`Goed${n}_Aantal`])||0;
        if(jaar===ditJaar){gewMaand[mnd]+=g;}
        totGewicht+=g; totColli+=c;
        const soort=a[`Goed${n}_Omschrijving`]||a[`Goed${n}_Soort`]||'';
        if(soort)goedMap[soort]=(goedMap[soort]||0)+1;
      });
      const land=a.LosLand||'';
      if(land){landKosten[land]=(landKosten[land]||0)+bedrag;landVol[land]=(landVol[land]||0)+1;}
      const van=a.LaadPlaats||a.LaadLand||'',los=a.LosPlaats||a.LosLand||'';
      if(van&&los){const r=`${van} → ${los}`;routeKosten[r]=(routeKosten[r]||0)+bedrag;routeVol[r]=(routeVol[r]||0)+1;}
      if(a.LaadDatum&&a.LosDatum){
        const d=Math.round((new Date(a.LosDatum)-new Date(a.LaadDatum))/(1000*60*60*24));
        if(d>0&&d<60){totDoorlooptijd+=d;aantalDoorlooptijd++;}
      }
      const sl=(a.Status||'').toLowerCase();
      let sLabel='In behandeling';
      if(sl.includes('afgerond')||sl.includes('afgeleverd'))sLabel='Afgerond';
      else if(isOnderweg(a))sLabel='Onderweg';
      else if(sl.includes('geoffreerd'))sLabel='Prijs ontvangen';
      else if(sl.includes('opdracht'))sLabel='Bevestigd';
      statusMap[sLabel]=(statusMap[sLabel]||0)+1;
    });

    const totaalKosten=kostenMaand.reduce((s,v)=>s+v,0);
    const totaalVol=aanvragen.filter(a=>new Date(a.Datum||0).getFullYear()===ditJaar).length;
    const gemKosten=totaalVol>0?totaalKosten/totaalVol:0;
    const gemDoorloop=aantalDoorlooptijd>0?Math.round(totDoorlooptijd/aantalDoorlooptijd):null;
    const toSlices=(map,top=6)=>Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,top).map(([label,value],i)=>({label,value,color:PALETTE[i]}));
    const statusKleuren={'Afgerond':G.green,'Onderweg':G.navy,'Bevestigd':G.navyLight,'Prijs ontvangen':G.gold,'In behandeling':'#c2e6ef'};
    const statusLabels={'Afgerond':t('status_afgerond'),'Onderweg':t('status_onderweg'),'Bevestigd':t('status_opdracht_bevestigd'),'Prijs ontvangen':t('status_prijs_ontvangen'),'In behandeling':t('status_in_behandeling')};

    return {
      totaalKosten,totaalVol,gemKosten,gemDoorloop,totGewicht,totColli,
      afgerond:statusMap['Afgerond']||0, onderweg:statusMap['Onderweg']||0,
      periodeData:{
        maand:{labels:MAANDEN_NL,kosten:kostenMaand,vol:volMaand,gew:gewMaand,hl:huidigeMaand},
        kwartaal:{labels:['Q1','Q2','Q3','Q4'],kosten:kostenKwart,vol:volKwart,gew:Array(4).fill(0),hl:Math.floor(huidigeMaand/3)},
        jaar:{labels:jaarRange.map(String),kosten:kostenJaar,vol:volJaar,gew:Array(3).fill(0),hl:2},
      },
      landKostenSlices:toSlices(landKosten),
      landVolSlices:toSlices(landVol),
      routeKostenSlices:toSlices(routeKosten),
      routeVolSlices:toSlices(routeVol),
      goedSlices:toSlices(goedMap),
      statusSlices:Object.entries(statusMap).map(([nlLabel,value])=>({label:statusLabels[nlLabel]||nlLabel,value,color:statusKleuren[nlLabel]||PALETTE[0]})),
    };
  },[aanvragen, PALETTE]);

  const MAANDEN_LABELS = useMemo(() => [
    t('maand_jan'),t('maand_feb'),t('maand_mrt'),t('maand_apr'),
    t('maand_mei'),t('maand_jun'),t('maand_jul'),t('maand_aug'),
    t('maand_sep'),t('maand_okt'),t('maand_nov'),t('maand_dec'),
  ], [t]);
  const pd=data.periodeData[periode];

  return (
    <div style={{paddingBottom:32}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:700,color:G.txt,margin:0,letterSpacing:'-.4px'}}>{t('analytics_titel')}</h2>
          <p style={{fontSize:13,color:G.txt3,margin:'3px 0 0'}}>{t('analytics_kosten_sub') || `${t('analytics_totale_kosten')}, ${t('nav_aanvragen').toLowerCase()}, routes — ${nu.getFullYear()}`}</p>
        </div>
        <div style={{display:'flex',background:G.white,border:`1px solid ${G.borderL}`,borderRadius:8,padding:3,gap:2}}>
          {[['maand', t('periode_maand')], ['kwartaal', t('periode_kwartaal')], ['jaar', t('periode_jaar')]].map(([p, label])=>(
            <button key={p} onClick={()=>setPeriode(p)} style={{
              padding:'5px 14px',border:'none',borderRadius:6,
              background:periode===p?G.navy:'transparent',
              color:periode===p?'#fff':G.txt2,
              fontSize:12.5,fontWeight:periode===p?600:400,
              cursor:'pointer',fontFamily:'inherit',transition:'.15s',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Kleurenpalet selector */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:600,color:G.txt3,letterSpacing:'.06em',textTransform:'uppercase',flexShrink:0}}>{t('kleurenpalet')}:</span>
        {Object.keys(PALETTE_COLORS).map((key)=>(
          <button key={key} onClick={()=>setPaletteKey(key)} style={{
            display:'flex',alignItems:'center',gap:6,padding:'5px 10px',
            border:`1.5px solid ${paletteKey===key?G.navy:G.borderL}`,
            borderRadius:20,background:paletteKey===key?G.navy:G.white,
            cursor:'pointer',transition:'.15s',
          }}>
            <div style={{display:'flex',gap:2}}>
              {PALETTE_COLORS[key].slice(0,5).map((c,i)=>(
                <div key={i} style={{width:10,height:10,borderRadius:'50%',background:c}}/>
              ))}
            </div>
            <span style={{fontSize:11.5,color:paletteKey===key?'#fff':G.txt2,fontWeight:paletteKey===key?600:400}}>{PALETTE_LABELS[key]}</span>
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,minmax(0,1fr))',gap:10,marginBottom:4}}>
        <KpiCard icon="💰" label={t('analytics_totale_kosten')} value={data.totaalKosten>0?fmt(data.totaalKosten):'—'} sub={`${data.totaalVol} ${t('zendingen_dit_jaar')}`} accent={`linear-gradient(90deg,${G.blue},#60a5fa)`}/>
        <KpiCard icon="📊" label={t('analytics_gem_zending')} value={data.gemKosten>0?fmt(data.gemKosten):'—'} sub={t('gem_per_transport')} accent={`linear-gradient(90deg,${G.gold},${G.goldLight})`}/>
        <KpiCard icon="✅" label={t('status_afgeleverd')} value={data.afgerond} sub={data.onderweg>0?`${data.onderweg} ${t('momenteel_onderweg')}`:t('geen_actieve_zendingen')} accent={`linear-gradient(90deg,${G.green},#34d399)`}/>
        <KpiCard icon="⚖️" label={t('analytics_totaal_gewicht')} value={data.totGewicht>0?fmtKg(data.totGewicht):'—'} sub={data.totColli>0?`${data.totColli} colli`:t('geen_colli_data')} accent={`linear-gradient(90deg,#8b5cf6,#a78bfa)`}/>
        <KpiCard icon="⏱" label={t('analytics_gem_doorlooptijd')} value={data.gemDoorloop?`${data.gemDoorloop} ${t('dgn')}`:'—'} sub={t('laad_los_datum')} accent={`linear-gradient(90deg,${G.amber},#fcd34d)`}/>
      </div>

      {/* Trends */}
      <SectionLabel>{t('trends_periode')}</SectionLabel>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:4}}>
        <div style={{background:G.white,border:`1px solid ${G.borderL}`,borderRadius:10,padding:'18px 20px'}}>
          <div style={{fontSize:11,fontWeight:600,color:G.txt2,letterSpacing:'.07em',textTransform:'uppercase',marginBottom:12}}>{t('analytics_totale_kosten')} — {periode === 'maand' ? t('periode_maand') : periode === 'kwartaal' ? t('periode_kwartaal') : t('periode_jaar')}</div>
          {data.totaalKosten===0
            ? <div style={{textAlign:'center',padding:'32px',color:G.txt3,fontSize:13}}>{t('analytics_geen_kosten')}</div>
            : <BarChart data={pd.kosten} labels={periode==='maand'?MAANDEN_LABELS:pd.labels} color={G.navy} height={150} formatY={fmtK} highlightIndex={pd.hl}/>}
        </div>
        <div style={{background:G.white,border:`1px solid ${G.borderL}`,borderRadius:10,padding:'18px 20px'}}>
          <div style={{fontSize:11,fontWeight:600,color:G.txt2,letterSpacing:'.07em',textTransform:'uppercase',marginBottom:12}}>{t('nav_aanvragen')} — {periode === 'maand' ? t('periode_maand') : periode === 'kwartaal' ? t('periode_kwartaal') : t('periode_jaar')}</div>
          {data.totaalVol===0
            ? <div style={{textAlign:'center',padding:'32px',color:G.txt3,fontSize:13}}>{t('nog_geen_data')}</div>
            : <BarChart data={pd.vol} labels={periode==='maand'?MAANDEN_LABELS:pd.labels} color={G.navyMid} height={150} formatY={fmtN} highlightIndex={pd.hl}/>}
        </div>
      </div>

      {/* Kostenverdeling */}
      <SectionLabel>{t('kostenverdeling')}</SectionLabel>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:4}}>
        <FlexChart palette={PALETTE} title={t('kosten_bestemming')} slices={data.landKostenSlices} formatVal={fmt} defaultType="donut" sub={t('analytics_op_losland')}/>
        <FlexChart palette={PALETTE} title={t('kosten_route')} slices={data.routeKostenSlices} formatVal={fmt} defaultType="taart" sub={t('analytics_laad_los_combo')}/>
      </div>

      {/* Volumeverdeling */}
      <SectionLabel>{t('volumeverdeling')}</SectionLabel>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:4}}>
        <FlexChart palette={PALETTE} title={t('zendingen_per_land')} slices={data.landVolSlices} formatVal={fmtN} defaultType="donut" sub={t('analytics_aantal_bestemming')}/>
        <FlexChart palette={PALETTE} title={t('meest_routes')} slices={data.routeVolSlices} formatVal={fmtN} defaultType="balk" sub={t('analytics_aantal_route')}/>
        <FlexChart palette={PALETTE} title={t('goederensoorten')} slices={data.goedSlices} formatVal={fmtN} defaultType="taart" sub={t('analytics_wat_vervoert')}/>
      </div>

      {/* Status + gewicht */}
      <SectionLabel>{t('status_gewicht')}</SectionLabel>
      <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:10}}>
        <FlexChart palette={PALETTE} title={t('status_zendingen')} slices={data.statusSlices} formatVal={fmtN} defaultType="donut"/>
        <div style={{background:G.white,border:`1px solid ${G.borderL}`,borderRadius:10,padding:'18px 20px'}}>
          <div style={{fontSize:11,fontWeight:600,color:G.txt2,letterSpacing:'.07em',textTransform:'uppercase',marginBottom:12}}>
            {t('gewicht_per_maand')} — {nu.getFullYear()}
          </div>
          {data.totGewicht===0
            ? <div style={{textAlign:'center',padding:'32px',color:G.txt3,fontSize:13}}>{t('analytics_geen_gewicht')}</div>
            : <BarChart data={data.periodeData.maand.gew} labels={MAANDEN_LABELS} color={G.navyLight} height={150} formatY={fmtKg} highlightIndex={data.periodeData.maand.hl}/>}
        </div>
      </div>
    </div>
  );
}
